import {
  ExtranetModal,
  ExtranetModalBody,
  ExtranetModalHeader,
  useLocalStorage,
} from '@pharmac-extranet/shared'
import { PharmacButton } from '@pharmac/ui'
import { addSeconds, differenceInSeconds } from 'date-fns'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useExtranetPath } from '../../router/useExtranetPath'
import { api } from '../../services/HttpService'

export const EXTRANET_INACTIVITY_TIMESTAMP_STORAGE_KEY = 'enactexpire'
export const EXTRANET_INACTIVITY_SETTINGS_STORAGE_KEY = 'enactcnfg'

const defaultSettings = {
  checkExpiryMilliseconds: 10 * 1000,
  updateCountdownMilliseconds: 1000,
  maxIdleSeconds: 30 * 60,
  popupThresholdSeconds: 5 * 60,
}

export const InactivityModal = () => {
  const navigate = useNavigate()
  const createLinkPath = useExtranetPath()
  const { t } = useTranslation('common')

  const [__SETTINGS] = useLocalStorage(EXTRANET_INACTIVITY_SETTINGS_STORAGE_KEY, defaultSettings)

  const [showModal, setShowModal] = useState<boolean>(false)
  const [timeLeft, setTimeLeft] = useState<string>('')

  const updateExpiry = () => {
    window.localStorage.setItem(
      EXTRANET_INACTIVITY_TIMESTAMP_STORAGE_KEY,
      String(+addSeconds(new Date(), __SETTINGS!.maxIdleSeconds))
    )
  }

  const getExpiry = () => {
    if (window.localStorage.getItem(EXTRANET_INACTIVITY_TIMESTAMP_STORAGE_KEY) === null) {
      updateExpiry()
    }

    return Number(window.localStorage.getItem(EXTRANET_INACTIVITY_TIMESTAMP_STORAGE_KEY)!)
  }

  const idleTimeInterval = useRef<NodeJS.Timeout>()
  const countDownInterval = useRef<NodeJS.Timeout>()
  const refreshTokenInterval = useRef<NodeJS.Timeout>()

  const clearIdleTimeInterval = () =>
    idleTimeInterval.current ? clearInterval(idleTimeInterval.current) : null
  const clearCountDownInterval = () =>
    countDownInterval.current ? clearInterval(countDownInterval.current) : null
  const clearRefreshTokenInterval = () =>
    refreshTokenInterval.current ? clearInterval(refreshTokenInterval.current) : null

  const refreshUserInfoAndToken = async () => {
    return await api.users.getLoggedInUser()
  }

  const clearAll = () => {
    window.localStorage.removeItem(EXTRANET_INACTIVITY_TIMESTAMP_STORAGE_KEY)
    clearIdleTimeInterval()
    clearCountDownInterval()
  }

  const handleLogout = () => {
    setShowModal(false)
    clearAll()
    navigate(createLinkPath('logout'))
  }

  const handleContinue = () => {
    setShowModal(false)
    clearCountDownInterval()
    clearRefreshTokenInterval()
    updateExpiry()
    refreshUserInfoAndToken()
  }

  useEffect(() => {
    updateExpiry()
  }, [])

  useEffect(() => {
    if (!showModal) {
      window.addEventListener('keypress', updateExpiry)
      window.addEventListener('mousedown', updateExpiry)

      if (refreshTokenInterval.current) {
        clearInterval(refreshTokenInterval.current)
      }

      idleTimeInterval.current = setInterval(() => {
        const secondsLeft = differenceInSeconds(new Date(getExpiry()), new Date())

        if (!showModal && secondsLeft <= __SETTINGS!.popupThresholdSeconds) {
          setShowModal(true)
        }
      }, __SETTINGS!.checkExpiryMilliseconds)
    }

    if (showModal) {
      window.removeEventListener('keypress', updateExpiry)
      window.removeEventListener('mousedown', updateExpiry)

      refreshTokenInterval.current = setInterval(
        () => {
          refreshUserInfoAndToken().then(() => {
            console.log('DEBUG :: InactivityModal :: tick')
          })
        },
        1 * 60 * 1000
      )
    }

    return () => {
      window.removeEventListener('keypress', updateExpiry)
      window.removeEventListener('mousedown', updateExpiry)

      if (idleTimeInterval.current) {
        clearInterval(idleTimeInterval.current)
      }

      if (refreshTokenInterval.current) {
        clearInterval(refreshTokenInterval.current)
      }
    }
  }, [showModal])

  const createTimeString = useCallback((secondsToExpiry: number) => {
    const minutes = Math.floor(secondsToExpiry / 60)
    const seconds = secondsToExpiry - minutes * 60

    if (minutes > 0) return `${minutes} ${t('timeoutMinutes')}`

    return `${seconds} ${t('timeoutSeconds')}`
  }, [])

  useEffect(() => {
    if (showModal) {
      clearIdleTimeInterval()

      countDownInterval.current = setInterval(() => {
        const secondsToExpiry = Math.floor((getExpiry() - +new Date()) / 1000)

        if (secondsToExpiry <= 0) {
          handleLogout()
        }

        setTimeLeft(createTimeString(secondsToExpiry))
      }, __SETTINGS!.updateCountdownMilliseconds)
    }

    return () => {
      clearCountDownInterval()
    }
  }, [showModal])

  if (!showModal) return null

  return createPortal(
    <ExtranetModal
      showOnProp={showModal}
      closeModal={handleContinue}
      buttons={[
        <PharmacButton
          key="logout-bnt"
          variant="secondary"
          onClick={handleLogout}
        >
          {t('no')}
        </PharmacButton>,
        <PharmacButton
          key="continue-btn"
          onClick={handleContinue}
        >
          {t('yes')}
        </PharmacButton>,
      ]}
    >
      <ExtranetModalHeader>{t('timeoutTitle')}</ExtranetModalHeader>
      <ExtranetModalBody>
        {t('timeoutContent1')} {timeLeft} {t('timeoutContent2')}
      </ExtranetModalBody>
    </ExtranetModal>,
    document.body
  )
}
